/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Jan 31 2024
 **/

import React, { useEffect, useMemo, useRef } from "react";
import { Subscription, timer } from "rxjs";

import { ShortDirectMessage } from "@/frameworks/designStuff/compounds";
import { usePopover } from "@/frameworks/core/hooks";
import { GROUP_POPOVER_ID, ZIndex } from "@/frameworks/core/commons";
import { AvatarStatusColorMap, MiniProfile } from "@/frameworks/core/components";
import { usePersistActions, usePersistStore } from "@/frameworks/core/persistence";
import { Configure } from "@/frameworks/libraries/utilities";
import { useChatListFocusScroll } from "../Chat/private";

type PanelRoomItemProps = {
	userIDList?: string[];
	hideUserIcon?: boolean;
	roomID?: string;
} & Omit<DesignStuff.ShortDirectMessage.Props, "avatarList">;

export default function PanelRoomItem({
	userIDList,
	hideUserIcon = false,
	isChatFeature = false,
	roomID,
	...rest
}: PanelRoomItemProps) {
	const popover = usePopover();
	const users = usePersistStore((state) => state.users);
	const dispatch = usePersistActions();

	useChatListFocusScroll(roomID);

	useEffect(() => {
		userIDList?.map((id) => {
			if (!dispatch.users.get_user(id)) {
				dispatch.users.refresh_user(id);
			}
		});
	}, []);

	const userDataList = useMemo(() => {
		if (hideUserIcon || !userIDList) return [];
		return userIDList.map((id) => dispatch.users.get_user(id));
	}, [hideUserIcon, userIDList, users]);

	const onMouseOverAvatar = (ev: MouseEvent) => {
		if (!ev.target) return;
		popover.show({
			popoverID: GROUP_POPOVER_ID.AVATAR_MINI_PROFILE,
			uniqueID: userDataList?.[0]?.id || "",
			content: (
				<div
					onMouseLeave={() => popover.hide(GROUP_POPOVER_ID.AVATAR_MINI_PROFILE)}
					style={{ width: "300px", height: "200px", paddingBottom: "10px" }}
				>
					<MiniProfile userID={userDataList?.[0]?.id || ""} />
				</div>
			),
			direction: {
				x: "left",
				y: "top"
			},
			position: {
				x: 0,
				y: -140
			},
			anchorEl: ev.target as HTMLElement,
			zIndex: ZIndex.SIDEBAR + 1
		});
	};

	const mouseEnterTimer = useRef<Subscription | null>(null);

	const onMouseEnter = (ev: Event) => {
		const nativeEvent = ev as MouseEvent;
		mouseEnterTimer.current = timer(500).subscribe(() => {
			onMouseOverAvatar(nativeEvent);
		});
	};

	const onMouseLeave = () => {
		if (mouseEnterTimer.current) {
			mouseEnterTimer.current.unsubscribe();
			mouseEnterTimer.current = null;
		}
	};

	return (
		<ShortDirectMessage
			avatarList={userDataList.map((user) => ({
				type: "image",
				kind: isChatFeature ? "people_32" : "people_24",
				text: user?.name?.substring(0, 1) ?? "",
				status: AvatarStatusColorMap[user?.activeStatus || "OFFLINE"],
				src: user?.profileImagePath || "",
				textColor: `${user?.colorProfile}_50_100` || "",
				onMouseEnter:
					userDataList.length === 1 ? (ev: React.MouseEvent) => onMouseEnter(ev.nativeEvent) : undefined,
				onMouseLeave: userDataList.length === 1 ? onMouseLeave : undefined
			}))}
			isChatFeature={isChatFeature}
			isMe={userIDList?.length === 1 && userIDList[0] === Configure.userID}
			isVoiceCall={rest.isVoiceCall}
			{...rest}
		/>
	);
}
